import {
  GET_OWN_OEM_SUPPORT_TICKET_BY_ID_QUERY,
  LIST_OWN_OEM_SUPPORT_TICKETS_QUERY,
} from "~/api";
import client from "~/apollo/_client";
import { useGetSupportTicketByIdForList } from "~/services";
import { transformSupportTicket } from "~/transformers/_supportTicketTransformer";
import { generateListOwnOemSupportTicketsVariables } from "~/utils/filter/_supportTicketQueryCond";

interface ReorderArgs {
  allTickets: any[];
  id: string;
  activeFilters?: Record<string, unknown>;
  searchQuery?: string;
  where?: Record<string, any>;
  isNewTicket?: boolean;
  isTicketDeleted?: boolean;
  status?: string;
}

/**
 * Support-ticket variant of `useReorderWorkOrderList`. Reads and writes the
 * `listOwnOemSupportTickets` cache so the list view re-orders optimistically on
 * create / status change / delete broadcasts.
 */
const useReorderSupportTicketList = () => {
  const { getSupportTicketById } = useGetSupportTicketByIdForList();

  const reOrderWithinList = ({
    allTickets,
    variables,
    position,
    status,
  }: any) => {
    client.cache.updateQuery(
      {
        query: LIST_OWN_OEM_SUPPORT_TICKETS_QUERY,
        variables,
      },
      ({ listOwnOemSupportTickets, ...rest }: any) => {
        const tickets = [
          ...(listOwnOemSupportTickets?.supportTickets || allTickets),
        ];
        const ticket = {
          ...tickets[position],
          status: status || tickets[position].status,
        };
        tickets.splice(position, 1);

        return {
          ...rest,
          listOwnOemSupportTickets: {
            ...listOwnOemSupportTickets,
            supportTickets: [ticket, ...tickets],
          },
        };
      },
    );
  };

  const removeFromList = ({ allTickets, variables, position }: any) => {
    client.cache.updateQuery(
      {
        query: LIST_OWN_OEM_SUPPORT_TICKETS_QUERY,
        variables,
      },
      ({ listOwnOemSupportTickets, ...rest }: any) => {
        const tickets = [
          ...(listOwnOemSupportTickets?.supportTickets || allTickets),
        ];
        tickets.splice(position, 1);
        return {
          ...rest,
          listOwnOemSupportTickets: {
            ...listOwnOemSupportTickets,
            supportTickets: tickets,
          },
        };
      },
    );
  };

  const appendTicketOnTop = ({
    id,
    variables,
    allTickets,
    isNewTicket = false,
  }: any) => {
    getSupportTicketById(id).then(
      ({ data: { getOwnOemSupportTicketById } = {} }: any) => {
        const ticket = transformSupportTicket(getOwnOemSupportTicketById);
        if (!ticket) return;
        client.cache.updateQuery(
          {
            query: LIST_OWN_OEM_SUPPORT_TICKETS_QUERY,
            variables,
          },
          (data: any) => {
            if (!data) return;

            const { listOwnOemSupportTickets, ...rest } = data;
            const tickets = [
              ...(listOwnOemSupportTickets?.supportTickets || allTickets),
            ];
            let newTicketList = tickets;
            if (tickets[0]?._id !== id) {
              newTicketList = [ticket, ...newTicketList];
            }

            return {
              ...rest,
              listOwnOemSupportTickets: {
                ...listOwnOemSupportTickets,
                ...(isNewTicket && {
                  totalCount: listOwnOemSupportTickets.totalCount + 1,
                }),
                supportTickets: newTicketList,
              },
            };
          },
        );
      },
    );
  };

  const handleReorderTicket = ({
    allTickets,
    id,
    activeFilters,
    searchQuery,
    where,
    isNewTicket,
    isTicketDeleted,
    status,
  }: ReorderArgs) => {
    const index = allTickets.findIndex((ticket) => ticket._id === id);

    const variables = generateListOwnOemSupportTicketsVariables({
      activeFilters,
      where,
      searchQuery,
    });

    // ticket is already on top
    if (index === 0 && !isTicketDeleted) {
      if (status) {
        reOrderWithinList({ position: index, allTickets, variables, status });
      }
      return;
    }

    if (isTicketDeleted && index >= 0) {
      removeFromList({
        position: index,
        allTickets,
        variables,
      });
      return;
    }

    // ticket exists somewhere in the list
    if (index > 0) {
      reOrderWithinList({ position: index, allTickets, variables, status });
      return;
    }

    // ticket does not exist in the list
    if (!isTicketDeleted && !where?.myTickets)
      appendTicketOnTop({
        id,
        variables,
        allTickets,
        isNewTicket,
      });
  };

  const handleUpdateTicketStatus = ({
    status,
    id,
  }: {
    status: any;
    id: string;
  }) => {
    const { getOwnOemSupportTicketById }: any = client.cache.readQuery({
      query: GET_OWN_OEM_SUPPORT_TICKET_BY_ID_QUERY,
      variables: {
        id,
      },
    });
    client.cache.writeQuery({
      query: GET_OWN_OEM_SUPPORT_TICKET_BY_ID_QUERY,
      variables: {
        id: id,
      },
      data: {
        getOwnOemSupportTicketById: {
          ...getOwnOemSupportTicketById,
          status,
        },
      },
    });
  };

  return { handleReorderTicket, handleUpdateTicketStatus };
};

export default useReorderSupportTicketList;
